import React from "react";
import { css } from "@emotion/css";
import { MathMarkdown } from "../../components/MathMarkdown";
import type { OfferCard, OverlayState } from "../../types/state";
import { OfferCards } from "./OfferCards";

/**
 * The wrap card — what the sitting came to, and what comes next.
 *
 * Everything above the cards is DETERMINISTIC: the server computes it
 * from what the sitting actually produced, so a learner always has their
 * receipt even when no model is available.  The coach's debrief, when it
 * arrives, streams into the ask lane beneath this card and never
 * replaces any of it.
 *
 * The three sections, and why each one is here:
 *
 * - **What you banked** — per world touched, in the planet's own words,
 *   with any rung crossed this sitting called out.  A crossing is
 *   permanent and rare; it should not scroll past as a bullet.
 * - **What pushed back** — graded misses by idea, with the AUTHOR's
 *   sentence for the misconception.  Not generated: the author already
 *   wrote something more specific than a model could say about an
 *   outcome it cannot see.
 * - **What now** — the dealer's cards, or a plain way out.
 */

const overlayBackdropStyle = css`
  position: fixed;
  inset: 0;
  background: var(--color-backdrop);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const overlayCardStyle = css`
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border-subtle);
  border-radius: 16px;
  padding: 28px;
  max-width: 520px;
  width: 90%;
  max-height: 84vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px var(--color-shadow-strong);
`;

const titleStyle = css`
  font-size: 1.3em;
  font-weight: 700;
  margin-bottom: 14px;
  color: var(--color-text);
`;

const wrapTextStyle = css`
  line-height: 1.6;
  margin-bottom: 18px;
  color: var(--color-text-secondary);
  p {
    margin: 0.5em 0;
  }
  strong {
    font-weight: 600;
  }
  ul {
    padding-left: 1.5em;
  }
`;

const sectionLabelStyle = css`
  font-size: 0.72em;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--color-text-muted);
  margin: 18px 0 8px;
`;

const rowStyle = css`
  padding: 11px 14px;
  border-radius: 10px;
  background: var(--color-bg-subtle);
  border: 1px solid var(--color-border-subtle);
  margin-bottom: 8px;
  line-height: 1.5;
`;

const rowTitleStyle = css`
  font-weight: 600;
  color: var(--color-text);
`;

const rowDetailStyle = css`
  font-size: 0.88em;
  color: var(--color-text-secondary);
  margin-top: 2px;
`;

const crossingStyle = css`
  display: inline-block;
  margin-top: 6px;
  padding: 2px 9px;
  border-radius: 999px;
  background: var(--color-primary-muted);
  border: 1px solid var(--color-primary);
  color: var(--color-text);
  font-size: 0.78em;
  font-weight: 600;
`;

const tipStyle = css`
  margin-top: 5px;
  padding-left: 10px;
  border-left: 2px solid var(--color-border);
  font-size: 0.88em;
  color: var(--color-text-secondary);
`;

const openLoopStyle = css`
  margin: 4px 0 8px;
  padding: 14px 16px;
  border-radius: 12px;
  background: var(--color-primary-muted);
  border-left: 3px solid var(--color-primary);
  line-height: 1.55;
  color: var(--color-text);
  p { margin: 0.4em 0; }
  p:first-of-type { margin-top: 0; }
  p:last-of-type { margin-bottom: 0; }
  strong { font-weight: 600; }
`;

const completeButtonStyle = css`
  width: 100%;
  padding: 13px;
  border: none;
  border-radius: 10px;
  background: var(--color-primary);
  color: var(--color-text-inverse);
  font-size: 1em;
  font-weight: 600;
  cursor: pointer;
  margin-top: 16px;
  transition: background 0.15s ease;

  &:hover {
    background: var(--color-primary-hover);
  }
`;

const quietButtonStyle = css`
  width: 100%;
  padding: 11px;
  border: 1px solid var(--color-border-subtle);
  border-radius: 10px;
  background: transparent;
  color: var(--color-text-muted);
  font-size: 0.92em;
  cursor: pointer;
  margin-top: 10px;

  &:hover {
    color: var(--color-text);
    border-color: var(--color-border);
  }
`;

interface SessionWrapOverlayProps {
  overlay: Extract<OverlayState, { kind: "session_wrap" }>;
  /** Acknowledge and leave — the sitting is already closed server-side. */
  onComplete: () => void;
  /** Take one of the dealer's cards.  Absent = the cards are not offered. */
  onTakeOffer?: (card: OfferCard) => void;
  onOtherWaters?: () => void;
  /** A commit is in flight. */
  busy?: boolean;
}

export const SessionWrapOverlay: React.FC<SessionWrapOverlayProps> = ({
  overlay,
  onComplete,
  onTakeOffer,
  onOtherWaters,
  busy = false,
}) => {
  // Overlays are interaction-shaped: kind-declared content fields live
  // at the overlay root by name.  session_wrap declares `summary` as
  // its stream field.
  const wrapText = overlay.summary || "";
  const openLoop = overlay.openLoop || "";
  const achievements = overlay.achievements ?? [];
  const struggles = overlay.struggles ?? [];
  const offer = overlay.offer ?? null;
  const released = overlay.route?.released ?? false;

  return (
    <div className={overlayBackdropStyle} data-testid="session-wrap-overlay">
      <div className={overlayCardStyle}>
        <div className={titleStyle}>{released ? "Wrapped up" : "That's the sitting"}</div>

        {wrapText && (
          <div className={wrapTextStyle}>
            <MathMarkdown>{wrapText}</MathMarkdown>
          </div>
        )}

        {achievements.length > 0 && (
          <div data-testid="session-wrap-achievements">
            <div className={sectionLabelStyle}>What you banked</div>
            {achievements.map((entry) => (
              <div className={rowStyle} key={entry.planet}>
                <div className={rowTitleStyle}>{entry.title}</div>
                {entry.sentence && <div className={rowDetailStyle}>{entry.sentence}</div>}
                {entry.crossed.map((rung) => (
                  <div className={crossingStyle} key={rung} data-testid="session-wrap-crossing">
                    crossed {rung}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {struggles.length > 0 && (
          <div data-testid="session-wrap-struggles">
            <div className={sectionLabelStyle}>What pushed back</div>
            {struggles.map((entry) => (
              <div className={rowStyle} key={entry.claim}>
                <div className={rowTitleStyle}>{entry.title}</div>
                {entry.burned && (
                  <div className={rowDetailStyle}>you were sure about this one</div>
                )}
                {entry.tip && <div className={tipStyle}>{entry.tip}</div>}
              </div>
            ))}
          </div>
        )}

        {openLoop && (
          <div data-testid="session-wrap-open-loop">
            <div className={sectionLabelStyle}>Next time</div>
            <div className={openLoopStyle}>
              <MathMarkdown>{openLoop}</MathMarkdown>
            </div>
          </div>
        )}

        {onTakeOffer && offer && offer.cards.length > 0 && (
          <div data-testid="session-wrap-offer">
            <div className={sectionLabelStyle}>What now</div>
            <OfferCards
              offer={offer}
              onTake={onTakeOffer}
              onOtherWaters={onOtherWaters}
              busy={busy}
            />
            <button
              type="button"
              className={quietButtonStyle}
              onClick={onComplete}
              data-testid="complete-session-button"
            >
              Finish for now
            </button>
          </div>
        )}

        {(!onTakeOffer || !offer || offer.cards.length === 0) && (
          <button
            className={completeButtonStyle}
            onClick={onComplete}
            data-testid="complete-session-button"
          >
            Finish for now
          </button>
        )}
      </div>
    </div>
  );
};
